import React, { Suspense, ElementType, ErrorInfo, ReactNode, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';

// project imports
import Loader from './Loader';

const SLOW_LOAD_MS = 15000;

const PAGE_MESSAGES = {
    slow: {
        key: 'common.page_loading_slow',
        defaultValue: 'This is taking longer than expected. Please check your connection or reload the page.'
    },
    error: {
        key: 'common.page_load_error',
        defaultValue: "We couldn't load this page. Please check your connection and try again."
    }
};

// Can render before the portal config (and therefore i18n) has loaded, so it must not suspend
const PageMessage = ({ type }: { type: keyof typeof PAGE_MESSAGES }) => {
    const { t } = useTranslation(undefined, { useSuspense: false });
    const message = PAGE_MESSAGES[type];
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                py: 6,
                px: 2,
                width: '100%',
                textAlign: 'center'
            }}
        >
            <Typography variant="subtitle1">{t(message.key, { defaultValue: message.defaultValue })}</Typography>
            <Button variant="contained" onClick={() => window.location.reload()}>
                {t('common.reload', { defaultValue: 'Reload' })}
            </Button>
        </Box>
    );
};

const PageLoader = () => {
    const [slow, setSlow] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => setSlow(true), SLOW_LOAD_MS);
        return () => clearTimeout(timer);
    }, []);
    return (
        <>
            <Loader />
            {slow && <PageMessage type="slow" />}
        </>
    );
};

class PageErrorBoundary extends React.Component<{ children: ReactNode; resetKey: string }, { hasError: boolean }> {
    state = { hasError: false };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        Sentry.captureException(error, { contexts: { react: { componentStack: info.componentStack } } });
    }

    // Routes that reuse the same component (e.g. account add/edit) must retry after navigation instead of keeping the error
    componentDidUpdate(prevProps: { resetKey: string }) {
        if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
            this.setState({ hasError: false });
        }
    }

    render() {
        if (this.state.hasError) {
            return <PageMessage type="error" />;
        }
        return this.props.children;
    }
}

// ==============================|| LOADABLE - LAZY LOADING ||============================== //

const LoadableRoute = ({ lazyComponent: LazyComponent, ...props }: { lazyComponent: ElementType; [key: string]: any }) => {
    const location = useLocation();
    return (
        <PageErrorBoundary resetKey={location.key}>
            <Suspense fallback={<PageLoader />}>
                <LazyComponent {...props} />
            </Suspense>
        </PageErrorBoundary>
    );
};

const Loadable = (LazyComponent: ElementType) => (props: any) => <LoadableRoute lazyComponent={LazyComponent} {...props} />;

export default Loadable;
